import React, { useRef, useEffect, useState } from "react";
import { BUSINESS_NAME } from "@/core/constants/business";
import { useAutoplayVideo } from "@/core/utils/autoplay-videos";
import {
  animateTitle,
  animateSubtitle,
  animateOverlay,
  cleanupAnimations,
} from "./gsap";

const Hero: React.FC = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Use the autoplay video hook to ensure the video plays
  useAutoplayVideo(videoRef);

  // Use effect to properly position the hero section on mount and resize
  useEffect(() => {
    const fixHeroPosition = () => {
      if (!sectionRef.current) return;

      // Get actual header height
      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-total-height"
        ) || "84"
      );

      // Get all header elements to ensure proper z-index
      const headerElements = document.querySelectorAll("header");
      headerElements.forEach((header) => {
        if (header) header.style.zIndex = "100";
      });

      // Set precise positioning for the hero section
      sectionRef.current.style.marginTop = "0";
      sectionRef.current.style.paddingTop = `${headerHeight}px`;
    };

    // Run once on mount
    fixHeroPosition();

    // Add resize listener
    window.addEventListener("resize", fixHeroPosition);

    // Cleanup
    return () => {
      window.removeEventListener("resize", fixHeroPosition);
    };
  }, []);

  // Animation effect
  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (titleRef.current && subtitleRef.current && overlayRef.current) {
        animateTitle({ current: titleRef.current });
        animateSubtitle({ current: subtitleRef.current });
        animateOverlay({ current: overlayRef.current });
      }
    }, 100);

    return () => {
      clearTimeout(animationTimeout);
      cleanupAnimations();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="contact-hero-title"
      className="relative bg-law-black text-white overflow-hidden h-[60vh] max-h-[480px] min-h-[300px]"
    >
      {/* Background wrapper that will extend under the header */}
      <div
        className="absolute inset-0 top-[-var(--header-total-height)] bottom-0 left-0 right-0 w-full"
        style={{ height: "calc(100% + var(--header-total-height))" }}
        aria-hidden="true"
      >
        {/* Poster image with immediate load */}
        <div
          className={`absolute inset-0 w-full h-full ${
            videoLoaded ? "opacity-0" : "opacity-100"
          } transition-opacity duration-500`}
          style={{
            backgroundImage:
              "url('/videos/street-lawyer-services-cannabis-festival-washington-dc-poster.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center center",
          }}
          role="presentation"
          aria-hidden="true"
        ></div>

        {/* Video */}
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover ${
            videoLoaded ? "opacity-100" : "opacity-0"
          } transition-opacity duration-500`}
          autoPlay
          muted
          loop
          playsInline
          poster="/videos/street-lawyer-services-cannabis-festival-washington-dc-poster.jpg"
          onLoadedData={() => setVideoLoaded(true)}
          onCanPlayThrough={() => setVideoLoaded(true)}
          aria-hidden="true"
        >
          <source
            src="/videos/street-lawyer-services-cannabis-festival-washington-dc.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>

        {/* Overlay */}
        <div
          ref={overlayRef}
          className="absolute inset-0 bg-gradient-to-b from-law-black/80 to-law-black/60"
          aria-hidden="true"
        ></div>
      </div>

      {/* Content - positioned correctly with padding for the header */}
      <div className="container mx-auto px-4 h-full flex items-center justify-center relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            id="contact-hero-title"
            ref={titleRef}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Contact {BUSINESS_NAME}
          </h1>
          <p ref={subtitleRef} className="text-lg md:text-xl">
            Have questions about our cannabis products or services? We're here
            to help.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
